// AGC: ManualPolygonGeometry - Define field boundaries by tapping on map
// AGC: User taps to add vertices, polygon is drawn when ≥3 vertices
// AGC: Uses Google Maps API for satellite imagery

import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { GoogleMap, useJsApiLoader, Polygon, Marker } from '@react-google-maps/api'
import { Undo2, Trash2, Check, MapPin, Loader2, X } from 'lucide-react'

// AGC: LocalStorage key for "don't show again" preference
const HIDE_MAP_INSTRUCTIONS_KEY = 'droidfarm-hide-map-instructions'
import { TopBar, GlassCard, Button } from '../shared'
import { useGpsAutoCenter } from '../../hooks/useGpsAutoCenter'
import { GOOGLE_MAPS_CONFIG, MAP_CONFIG } from '@droidfarm/shared'
import { calculatePolygonArea, calculatePerimeter, formatAreaHa, formatPerimeterM, isValidPolygon } from '../../utils/geometry'
import type { Screen, PlotFormData } from '../../types'

interface ManualPolygonGeometryProps {
  plotData: PlotFormData
  onNavigate: (screen: Screen, data?: { plotData?: PlotFormData }) => void
  onBack: () => void
}

// AGC: Map container style - fills parent container completely
const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
}

// AGC: Polygon style options
const polygonOptions = {
  fillColor: '#FCD34D',
  fillOpacity: 0.5,
  strokeColor: '#D97706',
  strokeOpacity: 1,
  strokeWeight: 2,
  clickable: false,
  draggable: false,
  editable: false,
  geodesic: false,
  zIndex: 1,
}

/**
 * AGC: ManualPolygonGeometry - Manual polygon drawing screen with Google Maps
 */
export const ManualPolygonGeometry: React.FC<ManualPolygonGeometryProps> = ({ 
  plotData, 
  onNavigate, 
  onBack 
}) => {
  // AGC: Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
  })

  // AGC: State for vertices (Google Maps uses {lat, lng} format)
  const [vertices, setVertices] = useState<google.maps.LatLngLiteral[]>([])
  const [isFinalized, setIsFinalized] = useState(false)
  const [mapType, setMapType] = useState<'satellite' | 'roadmap'>('satellite')
  const [map, setMap] = useState<google.maps.Map | null>(null)

  // AGC: Convert vertices to [lat, lng] format for area calculation
  const verticesForCalc = useMemo(() => {
    return vertices.map(v => [v.lat, v.lng] as [number, number])
  }, [vertices])

  // AGC: Calculate area and perimeter
  const { area, perimeter } = useMemo(() => {
    if (verticesForCalc.length < 3) {
      return { area: 0, perimeter: 0 }
    }
    return {
      area: calculatePolygonArea(verticesForCalc),
      perimeter: calculatePerimeter(verticesForCalc),
    }
  }, [verticesForCalc])

  // AGC: Handle map click to add vertex
  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (isFinalized || !e.latLng) return
    
    const newVertex = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    }
    
    setVertices(prev => [...prev, newVertex])
    console.log('📍 AGC: Vertex added', newVertex)
  }, [isFinalized])

  // AGC: Undo last vertex
  const handleUndo = useCallback(() => {
    setVertices(prev => prev.slice(0, -1))
    setIsFinalized(false)
  }, [])

  // AGC: Clear all vertices
  const handleClear = useCallback(() => {
    setVertices([])
    setIsFinalized(false)
  }, [])

  // AGC: Finalize polygon
  const handleFinalize = useCallback(() => {
    if (!isValidPolygon(verticesForCalc)) {
      alert('É necessário pelo menos 3 pontos para criar um polígono')
      return
    }
    setIsFinalized(true)
    console.log('✅ AGC: Polygon finalized', { 
      vertices: vertices.length, 
      area, 
      perimeter 
    })
  }, [vertices, verticesForCalc, area, perimeter])

  // AGC: Save and continue
  const handleSave = useCallback(() => {
    // AGC: [AUDIT_FIX] Ensure ID exists for audit consistency
    const plotId = plotData.id || `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const updatedPlotData: PlotFormData = {
      ...plotData,
      id: plotId, // AGC: [AUDIT_FIX] Preserve or generate ID
      coordinates: verticesForCalc,
      area: area,
    }
    console.log('🆔 AGC: [AUDIT_FIX] ManualPolygon flow - ID:', plotId)
    onNavigate('crop-suggestions', { plotData: updatedPlotData })
  }, [plotData, verticesForCalc, area, onNavigate])

  // AGC: Map load callback
  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map)
    console.log('🗺️ AGC: Google Map loaded')
  }, [])

  // AGC: Auto-center the Google Map on the user's current GPS location once
  // AGC: the map is ready, but only when there are no pre-existing vertices
  // AGC: to keep in view (i.e. starting fresh, not editing). Native uses
  // AGC: internal GPS; web prompts the browser for geolocation permission.
  useGpsAutoCenter({
    enabled: !!map && (!plotData.coordinates || plotData.coordinates.length === 0),
    onLocate: (coords) => {
      if (map) {
        map.panTo({ lat: coords.latitude, lng: coords.longitude })
        map.setZoom(Math.max(map.getZoom() ?? 15, 16))
      }
    },
  })

  // AGC: Map unload callback
  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  // AGC: Toggle map type
  const toggleMapType = useCallback(() => {
    setMapType(prev => prev === 'satellite' ? 'roadmap' : 'satellite')
  }, [])

  // AGC: Loading state
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] flex flex-col">
        <TopBar 
          title="Desenhar no Mapa"
          subtitle="Passo 3 de 4"
          onBack={onBack}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-[#0B5F4A] animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Carregando Google Maps...</p>
          </div>
        </div>
      </div>
    )
  }

  // AGC: Error state
  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] flex flex-col">
        <TopBar 
          title="Desenhar no Mapa"
          subtitle="Passo 3 de 4"
          onBack={onBack}
        />
        <div className="flex-1 flex items-center justify-center p-4">
          <GlassCard className="bg-red-50 border-red-200">
            <div className="text-center">
              <p className="font-medium text-red-800">Erro ao carregar Google Maps</p>
              <p className="text-sm text-red-600 mt-2">{loadError.message}</p>
              <Button variant="secondary" onClick={onBack} className="mt-4">
                Voltar
              </Button>
            </div>
          </GlassCard>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen bg-[#F4F5F7] flex flex-col overflow-hidden">
      {/* AGC: Top Bar */}
      <div className="flex-shrink-0">
        <TopBar 
          title="Desenhar no Mapa"
          subtitle="Passo 3 de 4"
          onBack={onBack}
        />
      </div>

      {/* AGC: Progress indicator */}
      <div className="flex-shrink-0 px-4 py-2">
        <div className="flex gap-2">
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-gray-200" />
        </div>
      </div>

      {/* AGC: Field info and stats */}
      <div className="flex-shrink-0 px-4 py-2 flex gap-2">
        <GlassCard className="flex-1" padding="sm">
          <div className="text-center">
            <p className="text-xs text-gray-500">Vértices</p>
            <p className="text-lg font-bold text-gray-900">{vertices.length}</p>
          </div>
        </GlassCard>
        <GlassCard className="flex-1" padding="sm">
          <div className="text-center">
            <p className="text-xs text-gray-500">Área</p>
            <p className="text-lg font-bold text-[#0B5F4A]">
              {vertices.length >= 3 ? formatAreaHa(area) : '—'}
            </p>
          </div>
        </GlassCard>
        <GlassCard className="flex-1" padding="sm">
          <div className="text-center">
            <p className="text-xs text-gray-500">Perímetro</p>
            <p className="text-lg font-bold text-gray-900">
              {vertices.length >= 3 ? formatPerimeterM(perimeter) : '—'}
            </p>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Google Maps Container - flex-grow to fill ALL remaining vertical space */}
      <div className="flex-grow relative mx-4 mb-2 rounded-2xl overflow-hidden border border-gray-200">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={GOOGLE_MAPS_CONFIG.defaultCenter}
          zoom={GOOGLE_MAPS_CONFIG.defaultZoom}
          mapTypeId={mapType}
          onClick={handleMapClick}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            ...GOOGLE_MAPS_CONFIG.options,
            mapTypeId: mapType,
            mapTypeControl: false, // AGC: We use custom toggle
          }}
        >
          {/* AGC: Draw polygon when ≥3 vertices */}
          {vertices.length >= 3 && (
            <Polygon
              paths={vertices}
              options={polygonOptions}
            />
          )}
          
          {/* AGC: Draw vertex markers */}
          {vertices.map((vertex, index) => (
            <Marker
              key={`vertex-${index}`}
              position={vertex}
              label={{
                text: String(index + 1),
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: index === 0 ? 14 : 12,
                fillColor: index === 0 ? '#DC2626' : '#0B5F4A',
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 3,
              }}
            />
          ))}
        </GoogleMap>

        {/* AGC: Map type toggle */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={toggleMapType}
            className={`px-3 py-2 rounded-lg shadow-lg text-sm font-medium transition-colors ${
              mapType === 'satellite' 
                ? 'bg-[#0B5F4A] text-white' 
                : 'bg-white text-gray-700'
            }`}
          >
            {mapType === 'satellite' ? '🛰️ Satélite' : '🗺️ Mapa'}
          </button>
        </div>

        {/* AGC: Instructions overlay */}
        {vertices.length === 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 pointer-events-none">
            <GlassCard className="mx-4 pointer-events-auto">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-[#0B5F4A] mx-auto mb-3" />
                <p className="font-medium text-gray-900">Toque para adicionar vértices</p>
                <p className="text-sm text-gray-600 mt-2">
                  Marque os cantos do talhão tocando no mapa. 
                  É necessário pelo menos 3 pontos.
                </p>
              </div>
            </GlassCard>
          </div>
        )}
      </div>

      {/* AGC: Control buttons - flex-shrink-0 to maintain size */}
      <div className="flex-shrink-0 px-4 pb-4 space-y-2">
        {!isFinalized ? (
          <>
            {/* AGC: Drawing controls */}
            <div className="flex gap-3">
              <Button 
                variant="ghost" 
                onClick={handleUndo}
                icon={<Undo2 className="w-4 h-4" />}
                disabled={vertices.length === 0}
              >
                Desfazer
              </Button>
              
              <Button 
                variant="ghost"
                onClick={handleClear}
                icon={<Trash2 className="w-4 h-4" />}
                disabled={vertices.length === 0}
                className="text-red-600"
              >
                Limpar
              </Button>
            </div>

            {/* AGC: Finish button */}
            <Button 
              fullWidth 
              onClick={handleFinalize}
              disabled={vertices.length < 3}
              icon={<Check className="w-4 h-4" />}
            >
              Concluir lote
            </Button>
          </>
        ) : (
          <>
            {/* AGC: After polygon is finalized */}
            <GlassCard className="bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-emerald-800">Polígono definido!</p>
                  <p className="text-sm text-emerald-600">
                    Área: {formatAreaHa(area)} | Perímetro: {formatPerimeterM(perimeter)}
                  </p>
                </div>
              </div>
            </GlassCard>
            
            <div className="flex gap-3">
              <Button 
                variant="secondary" 
                onClick={() => setIsFinalized(false)}
              >
                Editar
              </Button>
              <Button 
                fullWidth
                onClick={handleSave}
              >
                Salvar lote
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ManualPolygonGeometry
